import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getUserService } from '@src/container';
import type { UserDTO } from '@src/application/users/dto/user.dto';
import type { UpdateUserInputDTO } from '@src/application/users/dto/user-input.dto';

interface UpdateUserVariables {
    userId: string;
    data: UpdateUserInputDTO;
}

/**
 * Hook: useUpdateUser
 * Updates user information (Admin only)
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for updating user
 * 
 * @example
 * ```typescript
 * const { mutate: updateUser, isPending, error } = useUpdateUser({
 *     onSuccess: (updatedUser) => {
 *         console.log('User updated:', updatedUser);
 *     }
 * });
 * 
 * // Usage
 * updateUser({
 *     userId: 'user-id-123',
 *     data: { first_name: 'Jane', is_active: false }
 * });
 * ```
 */
export function useUpdateUser(
    options?: Omit<
        UseMutationOptions<UserDTO, Error, UpdateUserVariables>,
        'mutationFn'
    >
) {
    const userService = getUserService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async ({ userId, data }: UpdateUserVariables) => {
            return await userService.update(userId, data);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch users list
            await queryClient.invalidateQueries({ queryKey: ['users'] });

            // Update the specific user query
            queryClient.setQueryData(['users', variables.userId], data);

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
